import { Fade } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import { useReducer, useState } from "react"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import { ConfirmationDialog } from "@/components/common/Modals"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import type { CompanyRegistration } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import {
  initialState,
  registrationDialogReducer,
} from "@/screens/CompanySettings/Registration/components/CompanyRegistrationArea/registrationDialogReducer"
import RegistrationTabs from "@/screens/CompanySettings/Registration/components/CompanyRegistrationArea/RegistrationTabs"
import { RESET, SET_DIALOG, SET_REGISTRATION, SET_TAB } from "@/screens/CompanySettings/Registration/constants"
import RegistrationFormDialog from "@/screens/CompanySettings/Registration/dialogs/RegistrationFormDialog"
import useCompanyRegistration from "@/screens/CompanySettings/Registration/hooks/useCompanyRegistration"
import type { OpenRegistrationDialogParams } from "@/screens/CompanySettings/Registration/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const RegistrationArea = ({ companyId }: { companyId: string }): ReactElement => {
  const [{ openDialog, selectedTab, selectedRegistration }, dispatch] = useReducer(
    registrationDialogReducer,
    initialState
  )
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [registrationToDelete, setRegistrationToDelete] = useState<CompanyRegistration | null>(null)

  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { registrationList, registrationListFetching, refetchRegistrationList, deleteRegistration } =
    useCompanyRegistration({
      companyId,
    })
  const referenceIds = registrationList.map((registration) => registration.companyRegistrationId!) ?? []

  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceTableName: ReferenceTableName.CompanyRegistration,
    referenceIds,
    pause: !referenceIds.length,
  })

  const hasData = registrationList.length > 0
  const fetchingData = (registrationListFetching || fileFetching) && !hasData
  const noData = registrationList.length === 0 && !registrationListFetching

  const openRegistrationDialog = ({ tab, selected }: OpenRegistrationDialogParams = {}) => {
    dispatch({ type: SET_DIALOG, payload: true })
    dispatch({ type: SET_TAB, payload: tab })
    dispatch({ type: SET_REGISTRATION, payload: selected })
  }

  const closeRegistrationDialog = () => {
    dispatch({ type: RESET })
  }

  const refreshData = async () => {
    refetchRegistrationList()
    refetchFileUploadData()
  }

  const handleRemoveRegistration = (registration: CompanyRegistration) => {
    setRegistrationToDelete(registration)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!registrationToDelete?.companyRegistrationId) return

    const { error, data } = await deleteRegistration({
      input: { companyRegistrationId: registrationToDelete.companyRegistrationId },
    })

    const hasError =
      error?.message ||
      (data?.companyRegistration.delete.__typename === "CompanyRegistrationDeleteFailure" &&
        !!data?.companyRegistration.delete.error)

    setDeleteConfirmOpen(false)
    setRegistrationToDelete(null)

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to remove registration",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: `Company ${registrationToDelete.registrationType?.toLowerCase()} registration successfully removed`,
    })

    await refreshData()
  }

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false)
    setRegistrationToDelete(null)
  }

  return (
    <>
      {hasData && (
        <RegistrationTabs
          list={registrationList}
          fileData={fileList}
          openRegistrationDialog={openRegistrationDialog}
          onRemove={handleRemoveRegistration}
        />
      )}
      {fetchingData && <CircularLoadingIndicator />}
      {noData && (
        <Fade in={true} unmountOnExit timeout={500}>
          <InformationMissingAlert
            title='Add Registrations'
            action={
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                className='text-primary'
                startIcon={<AddOutlined />}
                onClick={() => openRegistrationDialog()}
              >
                Add Registrations
              </Button>
            }
          />
        </Fade>
      )}

      {openDialog && (
        <RegistrationFormDialog
          companyId={companyId}
          presetRegistrationType={selectedTab}
          data={selectedRegistration}
          onClose={closeRegistrationDialog}
          onSuccess={() => {
            closeRegistrationDialog()
            refreshData()
          }}
        />
      )}

      {deleteConfirmOpen && (
        <ConfirmationDialog
          title='Delete Registration'
          content={`Are you sure you want to delete ${registrationToDelete?.registrationType?.toLowerCase()} registration? This action cannot be undone.`}
          closeLabel='Cancel'
          confirmLabel='Yes, Delete'
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  )
}

export default RegistrationArea
